<template>
  <Teleport to="body">
    <Transition name="maintenance-fade">
      <div v-if="appStore.isMaintenanceMode" class="maintenance-overlay">
        <div class="maintenance-content">
          <q-icon :name="overlayIcon" class="maintenance-icon" />
          <div class="maintenance-title">{{ overlayTitle }}</div>
          <div class="maintenance-message">{{ overlaySubtitle }}</div>
          <div class="maintenance-countdown">
            {{ i18nStore.t('error.maintenance.autoRetry', { time: formattedCountdown }) }}
          </div>
          <div class="maintenance-actions">
            <OutlineButton
              white
              :label="i18nStore.t('error.maintenance.tryAgain')"
              :loading="isRetrying"
              @click="retryConnection"
            />
            <LinkButton
              class="m3-btn--on-dark"
              :label="i18nStore.t('error.maintenance.goHome')"
              @click="goHome"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAppStore } from 'src/stores/app';
import { useI18nStore } from 'src/stores/i18n';
import { api } from 'src/boot/api';
import { OutlineButton, LinkButton } from 'src/views/components/atoms/buttons';

const COUNTDOWN_SECONDS = 5 * 60;

const router = useRouter();
const appStore = useAppStore();
const i18nStore = useI18nStore();

const isRetrying = ref(false);
const countdown = ref(COUNTDOWN_SECONDS);
let countdownInterval: ReturnType<typeof setInterval> | null = null;

const formattedCountdown = computed(() => {
  const minutes = Math.floor(countdown.value / 60);
  const seconds = countdown.value % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

// Phase 5 of the deploy-fence track: branch overlay copy on the
// `error_type` discriminator. Falls back to the generic
// "Under Maintenance" copy for unknown / pre-phase-5 server responses.
const isDeploy = computed(() => appStore.maintenanceErrorType === 'deploy_in_progress');
const overlayIcon = computed(() => (isDeploy.value ? 'cloud_sync' : 'build'));
const overlayTitle = computed(() =>
  isDeploy.value
    ? i18nStore.t('error.maintenance.deploy.title')
    : i18nStore.t('error.maintenance.title'),
);
const overlaySubtitle = computed(() =>
  isDeploy.value
    ? i18nStore.t('error.maintenance.deploy.subtitle')
    : i18nStore.t('error.maintenance.subtitle'),
);

function startCountdown(): void {
  stopCountdown();
  countdown.value = COUNTDOWN_SECONDS;
  countdownInterval = setInterval(() => {
    countdown.value--;
    if (countdown.value <= 0) {
      void retryConnection();
    }
  }, 1000);
}

function stopCountdown(): void {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
}

function resetCountdown(): void {
  countdown.value = COUNTDOWN_SECONDS;
}

watch(
  () => appStore.isMaintenanceMode,
  (isVisible) => {
    if (isVisible) {
      startCountdown();
    } else {
      stopCountdown();
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  stopCountdown();
});

async function retryConnection(): Promise<void> {
  if (isRetrying.value) return;
  isRetrying.value = true;
  stopCountdown();
  try {
    // Probe a known unauthenticated endpoint. The poller's status
    // endpoint is the natural choice — same path the proactive poll
    // hits, same auth-exempt route.
    await api.get('/maintenance/status');
    appStore.clearMaintenanceMode();
  } catch {
    resetCountdown();
    startCountdown();
  } finally {
    isRetrying.value = false;
  }
}

function goHome(): void {
  stopCountdown();
  appStore.clearMaintenanceMode();
  void router.push('/');
}
</script>

<style lang="scss" scoped>
.maintenance-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.maintenance-content {
  text-align: center;
  color: #ffffff;
  padding: 2rem;
}

.maintenance-icon {
  font-size: 3rem;
  color: var(--q-primary);
  margin-bottom: 1.5rem;
}

.maintenance-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.maintenance-message {
  font-size: 1rem;
  opacity: 0.8;
  margin-bottom: 1rem;
}

.maintenance-countdown {
  font-size: 0.875rem;
  opacity: 0.6;
  margin-bottom: 2rem;
}

.maintenance-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.maintenance-fade-enter-active,
.maintenance-fade-leave-active {
  transition: opacity 0.3s ease;
}

.maintenance-fade-enter-from,
.maintenance-fade-leave-to {
  opacity: 0;
}
</style>
